/**
 * Derive a human-readable title for a node from its config, so that diff UI
 * can show meaningful labels instead of UUIDs.
 *
 * Mirrors the logic in `improvements/ImprovementOverlay.tsx` so the two
 * review surfaces stay consistent.
 */

const FALLBACK_BY_TYPE: Record<string, string> = {
  login: 'Login',
  navigate: 'Navigate',
  action: 'Action',
  assertion: 'Assertion',
  wait: 'Wait',
  condition: 'Condition',
  setup: 'Setup',
  api_sequence: 'API Sequence',
  end: 'End',
  seed: 'Seed',
  notification: 'Notification',
  mcp: 'MCP',
};

export function getNodeDisplayTitle(
  nodeType: string | undefined,
  config: Record<string, unknown> | undefined,
): string {
  const data = config ?? {};
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim().length > 0 ? v : undefined;

  const explicit =
    str(data.label) ?? str(data.customActionTitle) ?? str(data.title);
  if (explicit) return explicit;

  switch (nodeType) {
    case 'login':
      return str(data.credentialName) ?? 'Login';
    case 'navigate':
      return str(data.pageTitle) ?? str(data.url) ?? 'Navigate';
    case 'action':
      return str(data.actionDescription) ?? 'Action';
    case 'assertion':
      return str(data.condition) ?? str(data.description) ?? 'Assertion';
    case 'wait':
      return str(data.description) ?? 'Wait';
    case 'condition':
      return str(data.conditionDescription) ?? 'Condition';
    case 'setup':
      return str(data.setupFlowName) ?? 'Setup';
    case 'api_sequence':
      return str(data.sequenceName) ?? 'API Sequence';
    case 'mcp':
      return str(data.instructions) ?? 'MCP';
    default:
      return FALLBACK_BY_TYPE[nodeType ?? ''] ?? nodeType ?? 'Step';
  }
}
